import React, { InputHTMLAttributes, Ref, forwardRef } from 'react';

import Input from './Input';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  styleWidthSize?: string;
  leftIcon?: React.ReactNode;
  rightAction?: React.ReactNode;
  hasError?: boolean;
}

/**
 * 원본은 base -> hasError -> readOnly -> disabled 순으로 border-color 를 덮어썼다.
 * Tailwind 를 important 로 임포트하고 있어 경쟁 유틸리티의 승자가 CSS 출력 순서로
 * 정해지므로, 우선순위를 여기서 계산해 테두리 클래스를 하나만 붙인다.
 */
function borderClass(
  hasError?: boolean,
  readOnly?: boolean,
  disabled?: boolean,
) {
  if (disabled) {
    return 'border-apps-coreProductBorder focus-within:border-apps-corePrimary01';
  }
  if (readOnly) {
    return 'border-apps-coreProductBorder focus-within:border-apps-coreProductBorder';
  }
  if (hasError) {
    return 'border-apps-coreProductError01 focus-within:border-apps-coreProductError01';
  }
  return 'border-apps-coreProductBorder focus-within:border-apps-corePrimary01';
}

const InputBox = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      styleWidthSize = 'auto',
      leftIcon,
      rightAction,
      disabled,
      readOnly,
      hasError,
      className,
      ...props
    },
    ref: Ref<HTMLInputElement>,
  ) => {
    const surface =
      readOnly || disabled ? 'bg-apps-coreProductDisableContainer' : 'bg-apps-coreGrayWhite';

    return (
      <label
        className={`relative flex h-12 items-center gap-2 rounded-xl border border-solid px-4 py-2 ${surface} ${
          disabled ? 'text-apps-coreProductDisableContent' : ''
        } ${borderClass(hasError, readOnly, disabled)} ${className ?? ''}`}
        style={{ width: styleWidthSize }}
      >
        {leftIcon && <div className="flex-[1_0_0]">{leftIcon}</div>}
        <Input
          type={type}
          ref={ref}
          hasError={hasError}
          disabled={disabled}
          readOnly={readOnly}
          className="text-base leading-[140%] tracking-[-0.32px]"
          {...props}
        />
        {rightAction && <div className="flex-[1_0_0]">{rightAction}</div>}
      </label>
    );
  },
);

InputBox.displayName = 'InputBox';

export default InputBox;
